import * as React from 'react';
import { Select, Flex, SelectProps } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const MONTH_NAMES = [
  { key: 'months.january', fallback: 'Styczeń' },
  { key: 'months.february', fallback: 'Luty' },
  { key: 'months.march', fallback: 'Marzec' },
  { key: 'months.april', fallback: 'Kwiecień' },
  { key: 'months.may', fallback: 'Maj' },
  { key: 'months.june', fallback: 'Czerwiec' },
  { key: 'months.july', fallback: 'Lipiec' },
  { key: 'months.august', fallback: 'Sierpień' },
  { key: 'months.september', fallback: 'Wrzesień' },
  { key: 'months.october', fallback: 'Październik' },
  { key: 'months.november', fallback: 'Listopad' },
  { key: 'months.december', fallback: 'Grudzień' },
];

interface InputMonthProps {
  value: Date | undefined;
  min?: Date;
  max?: Date;
  onChange: (value: Date | undefined) => void;
  brColor?: string;
  isDisabled?: boolean;
}

export const InputMonth = ({
  value,
  min,
  max,
  onChange,
  isDisabled,
  brColor,
  ...props
}: InputMonthProps & Omit<SelectProps, 'onChange' | 'value'>): JSX.Element => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const minYear = min ? min.getFullYear() : currentYear - 5;
  const maxYear = max ? max.getFullYear() : currentYear;

  const years = React.useMemo(() => {
    const yearList = [];
    for (let i = minYear; i <= maxYear; i++) {
      yearList.push(i);
    }
    return yearList;
  }, [minYear, maxYear]);

  const selectedYear = value ? value.getFullYear() : '';
  const selectedMonth = value !== undefined ? value.getMonth() : '';
  const translatedMonthNames = React.useMemo(
    () => MONTH_NAMES.map(({ key, fallback }) => t(key, fallback)),
    [t],
  );

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val) {
      onChange(undefined);
      return;
    }

    const newYear = parseInt(val, 10);
    onChange(new Date(newYear, selectedMonth as number, 1));
  };

  // Handler zmiany miesiąca
  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === '') {
      onChange(undefined);
      return;
    }

    const newMonth = parseInt(val, 10);
    onChange(new Date(selectedYear as number, newMonth, 1));
  };

  return (
    <Flex gap={1}>
      <Select
        placeholder={t('year_one', 'Rok')}
        value={selectedYear}
        onChange={handleYearChange}
        isDisabled={isDisabled}
        borderColor={brColor}
        size={props.size}
      >
        {years.map(year => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </Select>

      <Select
        placeholder={t('month_one', 'Miesiąc')}
        value={selectedMonth}
        onChange={handleMonthChange}
        isDisabled={isDisabled}
        borderColor={brColor}
        size={props.size}
      >
        {translatedMonthNames.map((name, index) => (
          <option key={index} value={index}>
            {name}
          </option>
        ))}
      </Select>
    </Flex>
  );
};
